import type { LucideIcon } from "lucide-react";
import {
  type NavEntry,
  type NavLocation,
  type NavTarget,
  type RouteTarget,
  resolveTarget,
} from "./nav";
import { createRegistry } from "./registry";

/** What a command may use when it runs. */
export interface CommandContext {
  navigate(to: string | RouteTarget): void;
}

/** A route, or one built from where the user is (null: not available there). */
type Target =
  | { to: NavTarget | RouteTarget; run?: never }
  | { run(ctx: CommandContext): void; to?: never };
type Label = { labelKey: string; label?: never } | { label: string; labelKey?: never };

/** A palette entry: static ones carry an i18n key, provider results carry their own text. */
export type Command = Target &
  Label & {
    id: string;
    /** i18n key of the palette group it is listed under. */
    group: string;
    keywords?: string[];
    icon?: LucideIcon;
    /** Secondary text shown on the right, such as a kind or a project. */
    hint?: string;
  };

/** Dynamic results for a query, such as specs by name (registered by slice 1e). */
export interface CommandProvider {
  id: string;
  search(query: string, ctx: CommandContext): Promise<Command[]>;
}

/** The palette's command registries; the app has one, tests make their own. */
export function createCommandRegistry() {
  const commands = createRegistry<Command>("commands");
  const providers = createRegistry<CommandProvider>("command providers");
  return {
    register: commands.register,
    registerProvider: providers.register,
    commands: commands.list,
    providers: providers.list,
  };
}

export type CommandRegistry = ReturnType<typeof createCommandRegistry>;

const registry = createCommandRegistry();

export const registerCommand = (command: Command): void => registry.register(command);
export const registerCommandProvider = (provider: CommandProvider): void =>
  registry.registerProvider(provider);
export const commandRegistry = (): CommandRegistry => registry;

/** Navigation entries available at `at` as "Go to …" commands. */
export const navCommands = (entries: NavEntry[], at: NavLocation): Command[] =>
  entries.flatMap((entry) => {
    const to = resolveTarget(entry.to, at);
    return to
      ? [
          {
            id: `nav:${entry.id}`,
            group: "palette.groups.pages",
            labelKey: entry.labelKey,
            icon: entry.icon,
            to,
          },
        ]
      : [];
  });

/** Commands available at `at`, their targets resolved; those not available there are left out. */
export const commandsAt = (commands: Command[], at: NavLocation): Command[] =>
  commands.flatMap((command) => {
    if (command.to === undefined || typeof command.to === "object") return [command];
    const to = resolveTarget(command.to, at);
    return to ? [{ ...command, to }] : [];
  });

/** Every provider's results for a query; a failing provider is left out, not fatal. */
export async function searchProviders(
  providers: CommandProvider[],
  query: string,
  ctx: CommandContext,
): Promise<Command[]> {
  if (!query.trim()) return [];
  const settled = await Promise.allSettled(providers.map((p) => p.search(query, ctx)));
  return settled.flatMap((result) => (result.status === "fulfilled" ? result.value : []));
}
